import React, {useEffect, useRef} from "react";
import Loader from "@source/components/UI/loader/loader";
import styles from "./consentShared.module.scss";

export const ConsentAutoLoad = ({
  hasMore,
  isLoading,
  error,
  onLoadMore,
}: {
  hasMore: boolean;
  isLoading: boolean;
  error?: string;
  onLoadMore: () => Promise<void> | void;
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || isLoading || error) return;

    if (!("IntersectionObserver" in window)) {
      const loadWhenNearEnd = (event?: Event) => {
        const target = event?.target as HTMLElement | null;
        const isNearScrollableEnd = target && target.scrollHeight > target.clientHeight
          ? target.scrollHeight - (target.scrollTop + target.clientHeight) < 100
          : window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100;
        if (isNearScrollableEnd) onLoadMore();
      };
      document.addEventListener("scroll", loadWhenNearEnd, {capture: true, passive: true});
      loadWhenNearEnd();
      return () => document.removeEventListener("scroll", loadWhenNearEnd, {capture: true});
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) onLoadMore();
    }, {rootMargin: "120px"});
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [error, hasMore, isLoading, onLoadMore]);

  if (!hasMore && !isLoading) return null;
  return (
    <div className={styles.autoLoad} ref={sentinelRef} aria-live="polite">
      {isLoading && <Loader width={30} height={30}/>}
    </div>
  );
};
